import type { BranchChangesets } from '@festina/configuration-general-types/configuration'
import { LayerKey, STANDARD_BRANCH } from '@festina-life/api'
import {
  type ConfigurableServer,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import { useMutation } from '@tanstack/react-query'

import { ServerServices } from '../../openApiServices'

type Props = {
  server: ConfigurableServer
  branchName: string
  layer: Optional<LayerKey>
  block: Optional<string>
  shouldIncludeBaseBlocks: boolean
  generalBlocks: string[]
  shouldIncludeExtrinsicParameterValues: boolean
  shouldIncludeBaseLayers: boolean
  shouldIncludeBaseBranch: boolean
  generalLayerKeys: LayerKey[]
}

export const useLazyFetchBranchChangesets = () =>
  useMutation({
    mutationFn: async ({
      server,
      branchName,
      layer,
      shouldIncludeExtrinsicParameterValues,
      shouldIncludeBaseLayers,
      shouldIncludeBaseBranch,
      generalLayerKeys,
      block,
      shouldIncludeBaseBlocks,
      generalBlocks,
    }: Props) => {
      if (!server || !ServerServices[server]) {
        throw new Error(`Unknown server: ${server}`)
      }

      const isUsingConfigurationServer = branchName !== STANDARD_BRANCH
      const service = isUsingConfigurationServer
        ? ServerServices.configuration
        : ServerServices[server]

      const result =
        await service.api.ConfigurationRepositoryBranchesService.branchServiceGetGetBranchBlockChangesets(
          {
            componentName: server.toLocaleLowerCase(),
            branchName,
            includeBaseChangesets: shouldIncludeBaseBranch,
            skipLargeOperationTranslation: false,
            shouldIncludeExtrinsicParameterValues,
          },
        )

      if (!block && !layer) {
        return result
      }

      // We use client side filtering for layers and blocks given that the backend service doesn't consider base blocks for block filtering and it does with layers and that it doesn't filter by tenantId.
      return {
        _type: 'BranchChangesets',
        branchChangesets:
          result?.branchChangesets
            ?.filter(notEmpty)
            .filter((branchChangeset) => {
              const changesetLayer = new LayerKey({
                tenantId: branchChangeset.changeset?.tenantId,
                layerId: branchChangeset.changeset?.layer,
              })
              const isMatchingLayer = !layer || changesetLayer.equals(layer)
              const isGeneralLayer = generalLayerKeys.some((generalLayerKey) =>
                generalLayerKey.equals(changesetLayer),
              )
              const isMatchingBlock = branchChangeset.block === block
              const isGeneralBlock =
                branchChangeset.block &&
                generalBlocks.includes(branchChangeset.block)

              const isBlockIncluded =
                isMatchingBlock || (shouldIncludeBaseBlocks && isGeneralBlock)
              const isLayerIncluded =
                isMatchingLayer || (shouldIncludeBaseLayers && isGeneralLayer)

              return isBlockIncluded && isLayerIncluded
            }) ?? [],
      } satisfies BranchChangesets
    },
  })
